import type { CountryIsoCode } from '@/graphql/graphql';
import {
    CREATE_EXCLUSION_TEMPLATE,
    DELETE_EXCLUSION_TEMPLATE,
    GET_EXCLUSION_TEMPLATES,
    UPDATE_EXCLUSION_TEMPLATE,
} from '@/graphql/queries/exclusion-templates.graphql';
import { ApolloError } from '@apollo/client';
import { apolloClient } from './apollo-client';

export interface ExcludedCountryInput {
    countryISOCode: CountryIsoCode;
    exclusionType: string;
}

export interface ExclusionTemplate {
    id: string;
    name: string;
    excludedCountries: ExcludedCountryInput[];
    createdAt?: string;
    updatedAt?: string;
}

export interface CreateExclusionTemplateInput {
    name: string;
    excludedCountries: ExcludedCountryInput[];
}

export interface UpdateExclusionTemplateInput {
    id: string;
    name?: string;
    excludedCountries?: ExcludedCountryInput[];
}

interface UpdateExclusionTemplateMutationInput {
    templateId: string;
    templateName?: string;
    excludedCountries?: Array<{
        countryISOCode: string;
        exclusionType: string;
    }>;
}

interface ExclusionTemplateGraphQLResponse {
    identifier: {
        id: string;
        sku?: string;
    };
    templateName: string;
    excludedCountries: Array<{
        countryISOCode: string;
        exclusionType: string;
    }>;
    createdAt?: string;
    updatedAt?: string;
}

export async function createExclusionTemplate(input: CreateExclusionTemplateInput) {
    const result = await apolloClient.mutate({
        mutation: CREATE_EXCLUSION_TEMPLATE,
        variables: {
            input: {
                templateName: input.name,
                excludedCountries: input.excludedCountries,
            },
        },
    });

    const template = result.data.createExcludedCountriesTemplate;

    return {
        id: template.identifier.id,
        name: template.templateName,
        excludedCountries: template.excludedCountries,
        createdAt: template.createdAt,
        updatedAt: template.updatedAt,
    };
}

export async function fetchExclusionTemplates(): Promise<ExclusionTemplate[]> {
    const response = await apolloClient.query({
        query: GET_EXCLUSION_TEMPLATES,
        fetchPolicy: 'network-only',
    });

    const templates = response.data.searchExcludedCountriesTemplates?.excludedCountriesTemplates || [];

    return templates
        .map((template: ExclusionTemplateGraphQLResponse) => ({
            id: template.identifier.id,
            name: template.templateName,
            excludedCountries: template.excludedCountries,
            createdAt: template.createdAt,
            updatedAt: template.updatedAt,
        }))
        .sort((a: ExclusionTemplate, b: ExclusionTemplate) => a.name.localeCompare(b.name));
}

export async function updateExclusionTemplate(input: UpdateExclusionTemplateInput) {
    if (!input.name && !input.excludedCountries) {
        throw new Error('At least one of name or excludedCountries must be provided');
    }

    const cleanedExcludedCountries = input.excludedCountries
        ? input.excludedCountries.map(({ countryISOCode, exclusionType }) => ({
              countryISOCode,
              exclusionType,
          }))
        : undefined;

    const updateInput: UpdateExclusionTemplateMutationInput = {
        templateId: input.id,
    };

    if (input.name !== undefined) {
        updateInput.templateName = input.name;
    }

    if (cleanedExcludedCountries !== undefined) {
        updateInput.excludedCountries = cleanedExcludedCountries;
    }

    const result = await apolloClient.mutate({
        mutation: UPDATE_EXCLUSION_TEMPLATE,
        variables: {
            input: updateInput,
        },
    });

    const template = result.data.updateExcludedCountriesTemplate;

    return {
        id: template.identifier.id,
        name: template.templateName,
        excludedCountries: template.excludedCountries,
        createdAt: template.createdAt,
        updatedAt: template.updatedAt,
    };
}

export async function renameExclusionTemplate(id: string, newName: string, excludedCountries: ExcludedCountryInput[]) {
    return await updateExclusionTemplate({
        id,
        name: newName,
        excludedCountries,
    });
}

export async function deleteExclusionTemplate(id: string) {
    return await apolloClient.mutate({
        mutation: DELETE_EXCLUSION_TEMPLATE,
        variables: { templateId: id },
    });
}
